/**
 * Admin -> Settings -> Integrations: "AI hujjat o'qish" (Gemini). Kalit `.env` da (ko'rinmaydi); bu yerda yoqish/o'chirish,
 * qaysi turlar o'qiladi va shu oydagi sarf (docs/13-hujjatlar.md, 7.4).
 */
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useSettings, useUpdateSettings } from "@/features/admin/api";
import { useOcrStatus } from "@/features/documents/api";
import { kindLabel } from "@/features/documents/status";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/field";
import { ErrorState, Kv, Spinner } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { Toggle } from "@/components/ui/toggle";

// Faqat sxemasi bor turlar o'qiladi (backend `ocr.SCHEMAS`)
const READABLE = ["cdl", "medical_card"];

export function OcrCard() {
  const { t } = useTranslation("settings");
  const { t: td } = useTranslation("documents");
  const status = useOcrStatus();
  const settings = useSettings();
  const update = useUpdateSettings();

  if (status.isPending || settings.isPending) {
    return (
      <Card>
        <CardBody className="flex justify-center py-6">
          <Spinner />
        </CardBody>
      </Card>
    );
  }
  if (status.isError) return <ErrorState message={status.error.message} onRetry={() => status.refetch()} />;
  const s = status.data;
  const cfg = settings.data;
  const pill = !s.configured ? "notConfigured" : s.enabled ? "on" : "off";
  const types = (cfg?.document_types ?? []).filter((x) => READABLE.includes(x.key));

  function patch(data: { documents_ocr_enabled?: boolean; documents_ocr_kinds?: string[] }) {
    if (!cfg) return;
    update.mutate(
      { ...data, version: cfg.version },
      {
        onSuccess: () => {
          toast.success(t("ocr.saved"));
          void status.refetch();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("saveFailed")),
      },
    );
  }

  const m = s.month;
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {t("ocr.title")}
            <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold", pill === "on" ? "bg-status-hired text-ink" : "bg-fill text-ink")}>{t(`ocr.pill.${pill}`)}</span>
          </span>
        }
        subtitle={t("ocr.subtitle")}
      />
      <CardBody className="space-y-4 text-[13px]">
        {!s.configured && <p className="rounded-xl bg-fill px-4 py-3 text-muted">{t("ocr.notConfigured")}</p>}
        <div className="flex flex-wrap gap-x-10 gap-y-3">
          <Kv label={t("ocr.model")}>{s.model}</Kv>
          {s.auth && <Kv label={t("ocr.auth")}>{s.auth === "vertex" ? t("ocr.authVertex", { project: s.project }) : t("ocr.authKey")}</Kv>}
          <Kv label={t("ocr.month")}>
            {t("ocr.monthLine", { documents: m.documents, failed: m.failed, input: m.input_tokens.toLocaleString(), output: m.output_tokens.toLocaleString() })}
            {" · "}
            {m.cost_usd != null ? t("ocr.cost", { cost: m.cost_usd.toFixed(2) }) : t("ocr.costUnknown")}
          </Kv>
        </div>
        <Toggle label={t("ocr.enabled")} checked={s.enabled} disabled={!cfg || update.isPending} onChange={(v) => patch({ documents_ocr_enabled: v })} />
        <div>
          <div className="mb-1.5 text-[12px] font-medium text-ink-soft">{t("ocr.kinds")}</div>
          <div className="flex flex-wrap gap-4">
            {types.map((x) => (
              <Checkbox
                key={x.key}
                label={kindLabel(td, x.key, x.label)}
                checked={s.kinds.includes(x.key)}
                disabled={!cfg || update.isPending}
                onChange={(e) => patch({ documents_ocr_kinds: e.target.checked ? [...s.kinds, x.key] : s.kinds.filter((k) => k !== x.key) })}
              />
            ))}
          </div>
        </div>
        <p className="text-[12px] text-muted">{t("ocr.privacy")}</p>
      </CardBody>
    </Card>
  );
}
